/**
 * @rogueworlds/sim — deterministic fixed-step player simulation.
 *
 * This module is shared byte-for-byte by the client (local prediction) and the authoritative
 * server (Colyseus room, one room = one world shard), which is what makes reconciliation possible:
 * both sides run the same arithmetic over the same inputs and must land on the same state.
 *
 * Determinism rules:
 * - The tick length is always `1 / tickHz`; `PlayerCommand.dt` is the client's *measured* frame
 *   time and is carried for diagnostics only. It never influences the simulation.
 * - Only `+ - * /`, `Math.sqrt`, `Math.abs`, `Math.min`, `Math.max`, `Math.floor`, `Math.ceil`,
 *   `Math.imul`-free integer arithmetic and integer constants are used. No `Math.sin/cos/pow/exp`,
 *   no `Date`, no locale-sensitive API, no iteration over unordered collections: player order is
 *   always the ASCII-lexicographic sort of player ids (see `compareIds`).
 * - `Simulation.step` is the only mutating entry point; `reconcile` is pure.
 */

import type { PlayerState, Vec3, WorldConfig } from "@rogueworlds/shared";
import { DEFAULT_TICK_HZ, VOXEL_SIZE_M } from "@rogueworlds/shared";

/** Bit flags for `PlayerCommand.actions`. */
export const ACTION_JUMP = 1;
export const ACTION_SPRINT = 2;
export const ACTION_CROUCH = 4;

/** Horizontal acceleration in m/s^2 (full authority on the ground, scaled by AIR_CONTROL in air). */
export const ACCEL_M_S2 = 40;
/** Fraction of ACCEL_M_S2 available while airborne. */
export const AIR_CONTROL = 0.35;

/** Downward acceleration in m/s^2 (negative, Y is up). */
export const GRAVITY_M_S2 = -22;
/** Linear friction applied on the ground while there is no movement input, per second. */
export const GROUND_DRAG_PER_S = 12;
/** Linear friction applied while airborne, per second. */
export const AIR_DRAG_PER_S = 1.2;

/** Base horizontal speed cap in m/s. */
export const MAX_HORIZONTAL_M_S = 6;
/** Sprint speed multiplier; the cap becomes MAX_HORIZONTAL_M_S * SPRINT_MULTIPLIER. */
export const SPRINT_MULTIPLIER = 1.6;

/** Upward velocity granted by a jump from grounded state, in m/s. */
export const JUMP_SPEED_M_S = 6.5;
/** Collision column height in meters (the capsule top; the eye sits below it). */
export const PLAYER_HEIGHT_M = 1.7;
/** Half-extent of the square collision column in meters. */
export const PLAYER_RADIUS_M = 0.35;
/** Camera height above the player's feet in meters. */
export const EYE_HEIGHT_M = 1.6;

/** Hard pitch clamp in radians (~89 degrees), symmetric. */
const PITCH_LIMIT_RAD = 1.5533;
/** Coarse stride used when walking a body out of an overlap: one voxel. */
const CONTACT_STEP_M = VOXEL_SIZE_M;
/** Bisection steps after a free sample is found: 0.1 / 2^6 = 1.6 mm final resolution. */
const CONTACT_REFINE_ITERATIONS = 6;
/** Maximum overlap depth resolved in one call, in voxels (1.0 m). */
const CONTACT_MAX_SEARCH_STEPS = 10;

/** One tick of client intent. `move` is a horizontal (x, z) wish direction, `look` absolute yaw/pitch. */
export interface PlayerCommand {
  seq: number;
  dt: number;
  move: Vec3;
  look: Vec3;
  actions: number;
}

/** Server-side player state. `pos` is the feet position, in meters. */
export interface PlayerSimState {
  id: string;
  name: string;
  pos: Vec3;
  vel: Vec3;
  yaw: number;
  pitch: number;
  tick: number;
  grounded: boolean;
}

/** Voxel solidity query. Coordinates are INTEGER voxel coordinates, not meters. */
export type SolidQuery = (vx: number, vy: number, vz: number) => boolean;

type Axis = "x" | "y" | "z";

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function moveToward(current: number, target: number, maxDelta: number): number {
  const delta = target - current;
  if (delta > maxDelta) {
    return current + maxDelta;
  }
  if (delta < -maxDelta) {
    return current - maxDelta;
  }
  return target;
}

/** Unit-clamps the horizontal wish direction. Returns zeros for non-finite input. */
function clampWish(move: Vec3): { x: number; z: number } {
  const x = Number.isFinite(move.x) ? move.x : 0;
  const z = Number.isFinite(move.z) ? move.z : 0;
  const lengthSq = x * x + z * z;
  if (lengthSq <= 1) {
    return { x, z };
  }
  const inverse = 1 / Math.sqrt(lengthSq);
  return { x: x * inverse, z: z * inverse };
}

function axisValue(v: Vec3, axis: Axis): number {
  if (axis === "x") {
    return v.x;
  }
  return axis === "y" ? v.y : v.z;
}

function setAxisValue(v: Vec3, axis: Axis, value: number): void {
  if (axis === "x") {
    v.x = value;
  } else if (axis === "y") {
    v.y = value;
  } else {
    v.z = value;
  }
}

/**
 * Lowest/highest voxel index along one axis whose half-open voxel span `[v*S, (v+1)*S)` overlaps
 * the closed interval `[min, max]`. Touching a face exactly is NOT an overlap.
 */
function voxelMin(min: number): number {
  return Math.floor(min / VOXEL_SIZE_M - 1) + 1;
}

function voxelMax(max: number): number {
  return Math.ceil(max / VOXEL_SIZE_M) - 1;
}

/** True when the player's collision column at that position intersects at least one solid voxel. */
function overlapsSolid(px: number, py: number, pz: number, solid: SolidQuery): boolean {
  const minX = voxelMin(px - PLAYER_RADIUS_M);
  const maxX = voxelMax(px + PLAYER_RADIUS_M);
  const minY = voxelMin(py);
  const maxY = voxelMax(py + PLAYER_HEIGHT_M);
  const minZ = voxelMin(pz - PLAYER_RADIUS_M);
  const maxZ = voxelMax(pz + PLAYER_RADIUS_M);
  for (let vy = minY; vy <= maxY; vy++) {
    for (let vz = minZ; vz <= maxZ; vz++) {
      for (let vx = minX; vx <= maxX; vx++) {
        if (solid(vx, vy, vz)) {
          return true;
        }
      }
    }
  }
  return false;
}

/** True when the voxel layer directly beneath the feet is solid somewhere in the player's footprint. */
function footLayerSolid(px: number, py: number, pz: number, solid: SolidQuery): boolean {
  const supportLayer = Math.floor(py / VOXEL_SIZE_M) - 1;
  const minX = voxelMin(px - PLAYER_RADIUS_M);
  const maxX = voxelMax(px + PLAYER_RADIUS_M);
  const minZ = voxelMin(pz - PLAYER_RADIUS_M);
  const maxZ = voxelMax(pz + PLAYER_RADIUS_M);
  for (let vz = minZ; vz <= maxZ; vz++) {
    for (let vx = minX; vx <= maxX; vx++) {
      if (solid(vx, supportLayer, vz)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Advances one player by exactly one tick of `1 / tickHz` seconds: look, horizontal acceleration,
 * friction, the speed clamp, jump/gravity, then the position integration. Collisions are NOT part
 * of this function — run `resolveCollision` (or `stepPlayer`) afterwards.
 *
 * `cmd.dt` is ignored on purpose: the authoritative tick length comes from `tickHz`, so a client
 * with an unstable frame time cannot desynchronise the server.
 */
export function integrate(s: PlayerSimState, cmd: PlayerCommand, tickHz: number): void {
  const dt = 1 / (Number.isFinite(tickHz) && tickHz > 0 ? tickHz : DEFAULT_TICK_HZ);

  // Look: absolute yaw/pitch from the client, pitch clamped to the playable range.
  if (Number.isFinite(cmd.look.x)) {
    s.yaw = cmd.look.x;
  }
  if (Number.isFinite(cmd.look.y)) {
    s.pitch = clamp(cmd.look.y, -PITCH_LIMIT_RAD, PITCH_LIMIT_RAD);
  }

  const sprinting = (cmd.actions & ACTION_SPRINT) !== 0;
  const maxSpeed = MAX_HORIZONTAL_M_S * (sprinting ? SPRINT_MULTIPLIER : 1);
  const wish = clampWish(cmd.move);
  const wishX = wish.x * maxSpeed;
  const wishZ = wish.z * maxSpeed;

  // Horizontal acceleration toward the wish velocity. Air control scales acceleration only, so an
  // airborne player keeps their momentum but steers slowly.
  const accel = ACCEL_M_S2 * (s.grounded ? 1 : AIR_CONTROL) * dt;
  s.vel.x = moveToward(s.vel.x, wishX, accel);
  s.vel.z = moveToward(s.vel.z, wishZ, accel);

  // Linear friction `f = clamp(1 - drag*dt, 0, 1)`. It is applied only while the player asks for no
  // horizontal movement: with input held, acceleration alone owns the velocity, which is what makes
  // the clamped maximum speed reachable (GROUND_DRAG_PER_S * dt would otherwise dominate ACCEL_M_S2).
  if (wishX === 0 && wishZ === 0) {
    const dragPerSecond = s.grounded ? GROUND_DRAG_PER_S : AIR_DRAG_PER_S;
    const friction = clamp(1 - dragPerSecond * dt, 0, 1);
    s.vel.x *= friction;
    s.vel.z *= friction;
  }

  // Hard speed clamp, applied after acceleration so the cap can never be exceeded.
  const speedSq = s.vel.x * s.vel.x + s.vel.z * s.vel.z;
  if (speedSq > maxSpeed * maxSpeed) {
    const scale = maxSpeed / Math.sqrt(speedSq);
    s.vel.x *= scale;
    s.vel.z *= scale;
  }

  // Vertical: jump from the ground, rest on the ground, otherwise gravity.
  const wantsJump = (cmd.actions & ACTION_JUMP) !== 0;
  if (wantsJump && s.grounded) {
    s.vel.y = JUMP_SPEED_M_S;
    s.grounded = false;
  } else if (s.grounded) {
    // Resting contact. `resolveCollision` re-validates the voxel layer under the feet, so losing the
    // ground (walking off a ledge) clears `grounded` on the same tick.
    s.vel.y = 0;
  } else {
    s.vel.y += GRAVITY_M_S2 * dt;
  }

  s.pos.x += s.vel.x * dt;
  s.pos.y += s.vel.y * dt;
  s.pos.z += s.vel.z * dt;
  s.tick += 1;
}

/**
 * Places the player's collision column at `value` on one axis and reports whether it overlaps solids.
 */
function overlapsAt(s: PlayerSimState, solid: SolidQuery, axis: Axis, value: number): boolean {
  const x = axis === "x" ? value : s.pos.x;
  const y = axis === "y" ? value : s.pos.y;
  const z = axis === "z" ? value : s.pos.z;
  return overlapsSolid(x, y, z, solid);
}

/**
 * Resolves one axis, assuming the body currently overlaps solids: steps the axis back against the
 * direction of motion in one-voxel strides until the body is free, then bisects that last interval
 * so the body ends up on the contact plane (within CONTACT_STEP_M / 2^CONTACT_REFINE_ITERATIONS).
 * Zeroes the axis velocity, and marks the player grounded on a downward Y contact.
 */
function resolveAxis(s: PlayerSimState, solid: SolidQuery, axis: Axis): void {
  const velocity = axisValue(s.vel, axis);
  const direction = velocity > 0 ? 1 : -1;
  const collidingValue = axisValue(s.pos, axis);
  let freeValue = collidingValue;
  let foundFree = false;

  for (let step = 1; step <= CONTACT_MAX_SEARCH_STEPS; step++) {
    const candidate = collidingValue - direction * CONTACT_STEP_M * step;
    if (!overlapsAt(s, solid, axis, candidate)) {
      freeValue = candidate;
      foundFree = true;
      break;
    }
  }

  if (!foundFree) {
    // Buried deeper than CONTACT_MAX_SEARCH_STEPS voxels: retreat to the deepest testable sample and
    // stop the axis motion. (A later setBlock is the authority's problem, not the tick's.)
    freeValue = collidingValue - direction * CONTACT_STEP_M * CONTACT_MAX_SEARCH_STEPS;
  } else {
    let overlappingValue = collidingValue;
    let free = freeValue;
    for (let iteration = 0; iteration < CONTACT_REFINE_ITERATIONS; iteration++) {
      const mid = (overlappingValue + free) * 0.5;
      if (overlapsAt(s, solid, axis, mid)) {
        overlappingValue = mid;
      } else {
        free = mid;
      }
    }
    freeValue = free;
  }

  setAxisValue(s.pos, axis, freeValue);
  setAxisValue(s.vel, axis, 0);
  if (axis === "y" && direction < 0) {
    s.grounded = true;
  }
}

/**
 * Penetration resolver for the displacement `integrate` applied this tick.
 *
 * The contract signature has no `tickHz`, so this routine cannot know the tick's displacement
 * length; it is therefore dt-free by construction: it resolves the overlap that the integrated
 * position produced, walking the body back against the direction of motion. Axes are handled in
 * X -> Y -> Z order, re-testing the box before each axis because resolving X changes the Y/Z
 * overlap. A downward Y contact zeroes the vertical velocity and sets `grounded`.
 *
 * While there is no vertical motion, `grounded` is re-derived from the voxel layer directly below
 * the feet, which is how walking off a ledge leaves the player airborne.
 */
export function resolveCollision(s: PlayerSimState, solid: SolidQuery): void {
  if (s.vel.x !== 0 && overlapsSolid(s.pos.x, s.pos.y, s.pos.z, solid)) {
    resolveAxis(s, solid, "x");
  }
  if (s.vel.y !== 0 && overlapsSolid(s.pos.x, s.pos.y, s.pos.z, solid)) {
    resolveAxis(s, solid, "y");
  }
  if (s.vel.z !== 0 && overlapsSolid(s.pos.x, s.pos.y, s.pos.z, solid)) {
    resolveAxis(s, solid, "z");
  }
  if (s.vel.y === 0) {
    s.grounded = footLayerSolid(s.pos.x, s.pos.y, s.pos.z, solid);
  }
}

/** One full deterministic tick for one player: integrate, then resolve collisions. */
export function stepPlayer(s: PlayerSimState, cmd: PlayerCommand, tickHz: number, solid: SolidQuery): void {
  integrate(s, cmd, tickHz);
  resolveCollision(s, solid);
}

/** Creates a fresh player state. `spawn` is the feet position; the player starts airborne. */
export function initialState(id: string, name: string, spawn: Vec3): PlayerSimState {
  return {
    id,
    name,
    pos: { x: spawn.x, y: spawn.y, z: spawn.z },
    vel: { x: 0, y: 0, z: 0 },
    yaw: 0,
    pitch: 0,
    tick: 0,
    grounded: false,
  };
}

/** Projects a simulation state onto the frozen wire type (drops velocity and grounded). */
export function toPlayerState(s: PlayerSimState): PlayerState {
  return {
    id: s.id,
    name: s.name,
    pos: { x: s.pos.x, y: s.pos.y, z: s.pos.z },
    yaw: s.yaw,
    pitch: s.pitch,
    tick: s.tick,
  };
}

/** ASCII-lexicographic id ordering: locale-independent and identical on every runtime. */
function compareIds(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
}

function clonePlayerState(s: PlayerState): PlayerState {
  return {
    id: s.id,
    name: s.name,
    pos: { x: s.pos.x, y: s.pos.y, z: s.pos.z },
    yaw: s.yaw,
    pitch: s.pitch,
    tick: s.tick,
  };
}

/** Neutral command used for players that submitted no input this tick: gravity still applies. */
function neutralCommand(s: PlayerSimState, tickHz: number): PlayerCommand {
  return {
    seq: 0,
    dt: 1 / tickHz,
    move: { x: 0, y: 0, z: 0 },
    look: { x: s.yaw, y: s.pitch, z: 0 },
    actions: 0,
  };
}

/**
 * Server-authoritative reconciliation, pure and argument-preserving.
 *
 * Semantics:
 * - `authoritative.tick` is the newest tick the server has simulated (delivered in `ack`/`snapshot`).
 *   Everything the client predicted for ticks <= that value is already superseded by the server.
 * - History entries are the client's post-step predicted states, keyed by their own `tick`. Every
 *   entry with `tick <= authoritative.tick` is dropped. Entries with `tick > authoritative.tick` are
 *   re-applied in ascending tick order; because each entry was produced by one `stepPlayer` call on
 *   top of the previous one, the surviving entry with the highest tick already *is* the replay of all
 *   of them, so it is adopted as the result (ties: the last entry in array order wins).
 * - If the client's live `predicted` state is newer than the newest surviving history entry, it is
 *   adopted instead: the server has nothing newer to say about those ticks.
 * - `replayed` counts the history entries with `tick > authoritative.tick` that were carried over
 *   (the live `predicted` state is not counted).
 *
 * The returned state is always a fresh object; `predicted`, `authoritative` and `history` are never
 * mutated.
 */
export function reconcile(
  predicted: PlayerState,
  authoritative: PlayerState,
  history: PlayerState[],
): { state: PlayerState; replayed: number } {
  let newest = authoritative;
  let replayed = 0;
  for (const entry of history) {
    if (entry.tick <= authoritative.tick) {
      continue;
    }
    replayed += 1;
    if (entry.tick >= newest.tick) {
      newest = entry;
    }
  }
  if (predicted.tick > newest.tick) {
    newest = predicted;
  }
  return { state: clonePlayerState(newest), replayed };
}

/**
 * Fixed-timestep, authoritative player container for one world shard.
 *
 * `step` iterates players in ASCII-lexicographic id order (never Map insertion order), so the result
 * depends only on the player set and their commands, not on the order they were added. Input
 * ordering/idempotency (duplicate `seq`, replayed frames) belongs to the transport, not here.
 */
export class Simulation {
  private readonly cfg: WorldConfig;
  private readonly players = new Map<string, PlayerSimState>();
  private tickCounter = 0;

  constructor(cfg: WorldConfig) {
    this.cfg = cfg;
  }

  addPlayer(id: string, name: string, spawn: Vec3): void {
    const state = initialState(id, name, spawn);
    state.tick = this.tickCounter;
    this.players.set(id, state);
  }

  removePlayer(id: string): void {
    this.players.delete(id);
  }

  has(id: string): boolean {
    return this.players.has(id);
  }

  get(id: string): PlayerSimState | undefined {
    return this.players.get(id);
  }

  get size(): number {
    return this.players.size;
  }

  /** Advances every player by one tick and returns the resulting snapshot. */
  step(commands: Map<string, PlayerCommand>, solid: SolidQuery): PlayerState[] {
    this.tickCounter += 1;
    const tick = this.tickCounter;
    for (const state of this.orderedStates()) {
      const command = commands.get(state.id) ?? neutralCommand(state, this.cfg.tickHz);
      integrate(state, command, this.cfg.tickHz);
      state.tick = tick;
      resolveCollision(state, solid);
    }
    return this.getSnapshot(tick);
  }

  /** Replicated view of the shard. The `tick` argument stamps every entry. */
  getSnapshot(tick: number): PlayerState[] {
    const out: PlayerState[] = [];
    for (const state of this.orderedStates()) {
      const player = toPlayerState(state);
      player.tick = tick;
      out.push(player);
    }
    return out;
  }

  private orderedStates(): PlayerSimState[] {
    const states: PlayerSimState[] = [];
    for (const state of this.players.values()) {
      states.push(state);
    }
    states.sort((a, b) => compareIds(a.id, b.id));
    return states;
  }
}
